import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import "./VendorPortal.css";
import DynamicTable from "../../../components/DynamicTable/DynamicTable";
import Vendors from "./Venders";
import { useFormik } from "formik";
import * as Yup from "yup";
import { FocusError } from "focus-formik-error";
import { useDispatch } from "react-redux";
import { updateLoader } from "../../../_redux/LoaderSlice";
import { updateSnackbar } from "../../../_redux/SnackbarSlice";
import GetJobId from "../../../_utilities/GetJobId";
import {
  getApi,
  postApi,
  putApi,
  sessionExpired,
} from "../../../_redux/_apiHelpers/_apihelper";
import { DashboardPlusicon } from "../../../Icons/DashboardPlusicon";

const emptyVendor = {
  vendorName: "",
  registeredAddressHQ: "",
  website: "",
  contactNumber: "",
  representativeName: "",
  emailId: "",
  country: "",
  vendorStatus: "",
  billingAddressLine1: "",
  billingAddressLine2: "",
  city: "",
  state: "",
  zipcode: "",
};

const vendorStatusOptions = ["Active", "Inactive"];

const phoneRegExp = /^[0-9()+-\s\b]*$/;

const validationSchema = Yup.object({
  vendorName: Yup.string().required("Please enter vendor name"),
  registeredAddressHQ: Yup.string().required(
    "Please provide the registered address",
  ),
  website: Yup.string().test(
    "is-url-valid",
    "Invalid website format",
    (value) => {
      if (!value) return true;
      return /^((ftp|http|https):\/\/)?www\.([A-z0-9]+)\.([A-z0-9]{2,})/.test(
        value,
      );
    },
  ),
  contactNumber: Yup.string()
    .required("Please enter contact number")
    .min(10, "Contact number should be at least 10 characters")
    .max(20, "Contact number should be at most 20 characters")
    .matches(phoneRegExp, "Enter valid contact number"),
  representativeName: Yup.string().required(
    "Please enter representative's name",
  ),
  emailId: Yup.string()
    .email("Invalid email format")
    .required("Please enter email ID"),
  country: Yup.string().required("Please enter country"),
  vendorStatus: Yup.string().required("Please select vendor status"),
  billingAddressLine1: Yup.string().required(
    "Please enter billing address line 1",
  ),
  billingAddressLine2: Yup.string(),
  city: Yup.string()
    .required("Please enter city")
    .matches(/^[A-Za-z\s'-]+$/, "City can include only letters"),
  state: Yup.string()
    .required("Please enter state")
    .matches(/^[A-Za-z\s'-]+$/, "State can include only letters"),
  zipcode: Yup.string()
    .required("Please enter zipcode")
    .matches(/^[0-9]+$/, "Zipcode can include only numbers"),
});

// Table configuration for Vendor List
const configuration = {
  isSearchbar: true,
  isPaging: true,
  isDropdownActions: false,
  isCheckbox: false,
  isActions: false,
  recordperPage: 20,
  currentPage: 1,
  totalRecords: 0,
  limitData: 20,
  isCheckAlignRight: false,
  isPaginationTop: false,
  isSearchServer: true,
  isPagingServer: true,
};

const actionsDropdownData = ["Delete"];
const actionsList = { isDownload: false, isDelete: false };

const VendorPortal = () => {
  const [currentView, setCurrentView] = useState("list"); // list | create | view | edit
  const [activeTab, setActiveTab] = useState("details"); // 'details' | 'users'
  const [vendorsList, setVendorsList] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [filterValue, setFilterValue] = useState("");
  const [config, setConfig] = useState(configuration);
  const [isLoadDoc, setIsLoadDoc] = useState(true);

  const dispatch = useDispatch();
  const getJobCode = GetJobId();
  const initialSnackbar = { type: "", message: "" };

  useEffect(() => {
    if (isLoadDoc === true && getJobCode !== null) {
      let obj1 = { current: config.currentPage, offset: 1 };
      fetchData(obj1);
      setIsLoadDoc(false);
    }
  }, [isLoadDoc, getJobCode]);

  const fetchData = async (obj) => {
    setVendorsList([]);
    dispatch(updateLoader(true));
    dispatch(updateSnackbar(initialSnackbar));
    try {
      const res = await getApi(
        `/mgmt/customer/vendor?limit=${config.limitData}&offset=${obj.current}&keyword=${filterValue}&atsAdmin=true`,
        process.env.REACT_APP_USE_MANAGEMENT,
      );
      const jsonData = res.data;
      if (jsonData.code === 200) {
        const myData = jsonData.vendors;
        if (myData !== undefined && myData.length > 0) {
          myData.forEach((v) => {
            v.vendorStatus =
              String(v.vendorStatus) === "1" ? "Active" : "Inactive";
          });
          dispatch(updateLoader(false));
          let configure = JSON.parse(JSON.stringify(config));
          configure.totalRecords = jsonData.totalCount;
          configure.currentPage = obj.current;
          setConfig(configure);
          setVendorsList(myData);
        } else {
          dispatch(updateLoader(false));
          setVendorsList([]);
        }
      } else {
        dispatch(updateLoader(false));
        dispatch(
          updateSnackbar({
            type: "error",
            message: jsonData.message || "Failed to fetch vendors",
          }),
        );
      }
    } catch (error) {
      if (error?.response?.status === 405 || error?.response?.status === 406) {
        return sessionExpired(error?.response?.data?.message);
      }
      dispatch(updateLoader(false));
      dispatch(
        updateSnackbar({
          type: "error",
          message: error?.response?.data?.message || "Something went wrong",
        }),
      );
    }
  };

  const formik = useFormik({
    initialValues: { ...emptyVendor },
    validationSchema,
    onSubmit: async (values) => {
      if (currentView === "create") {
        dispatch(updateLoader(true));
        dispatch(updateSnackbar(initialSnackbar));

        const payload = {
          ...values,
          vendorStatus: values.vendorStatus === "Active" ? "1" : "0",
        };

        try {
          const res = await postApi(
            `/mgmt/customer/vendor?atsAdmin=true`,
            payload,
            process.env.REACT_APP_USE_MANAGEMENT,
          );
          const jsonData = res.data;
          if (jsonData.code === 200) {
            formik.resetForm();
            setCurrentView("list");
            setIsLoadDoc(true); // Refresh list
            setTimeout(() => {
              dispatch(updateLoader(false));
              dispatch(
                updateSnackbar({ type: "success", message: jsonData.message }),
              );
            }, 500);
          } else {
            dispatch(updateLoader(false));
            dispatch(
              updateSnackbar({
                type: "error",
                message: jsonData.message || "Failed to create vendor",
              }),
            );
          }
        } catch (error) {
          if (
            error?.response?.status === 405 ||
            error?.response?.status === 406
          ) {
            return sessionExpired(error?.response?.data?.message);
          }
          dispatch(updateLoader(false));
          dispatch(
            updateSnackbar({
              type: "error",
              message:
                error?.response?.data?.message || "Network error occurred",
            }),
          );
        }
      } else if (currentView === "edit") {
        dispatch(updateLoader(true));
        dispatch(updateSnackbar(initialSnackbar));

        const payload = {
          ...values,
          vendorStatus: values.vendorStatus === "Active" ? "1" : "0",
        };

        try {
          const res = await putApi(
            `/mgmt/customer/vendor/${values.id}?atsAdmin=true`,
            payload,
            process.env.REACT_APP_USE_MANAGEMENT,
          );
          const jsonData = res.data;
          if (jsonData.code === 200) {
            payload.vendorStatus =
              payload.vendorStatus === "1" ? "Active" : "Inactive";
            setSelectedVendor(payload);
            setCurrentView("view");
            setIsLoadDoc(true); // Refresh list in background
            setTimeout(() => {
              dispatch(updateLoader(false));
              dispatch(
                updateSnackbar({ type: "success", message: jsonData.message }),
              );
            }, 500);
          } else {
            dispatch(updateLoader(false));
            dispatch(
              updateSnackbar({
                type: "error",
                message: jsonData.message || "Failed to update vendor",
              }),
            );
          }
        } catch (error) {
          if (
            error?.response?.status === 405 ||
            error?.response?.status === 406
          ) {
            return sessionExpired(error?.response?.data?.message);
          }
          dispatch(updateLoader(false));
          dispatch(
            updateSnackbar({
              type: "error",
              message:
                error?.response?.data?.message || "Network error occurred",
            }),
          );
        }
      }
    },
  });

  // Setup columns configuration here
  const columnsData = [
    {
      id: 1,
      displayName: "Vendor Name",
      mappingName: "vendorName",
      theadClass: "dynatableheadtext text-center",
      tbodyClass: "dynatablebodytext text-center rm-link", // Added rm-link for styling
      type: "link",
      width: "",
      isEdit: true, // Tells DynamicTable to trigger onClick
      nagigate: "detailspage", // Triggers onReceiveActionProps("detailspage", obj)
    },
    {
      id: 2,
      displayName: "Email ID",
      mappingName: "emailId",
      theadClass: "dynatableheadtext text-center",
      tbodyClass: " dynatablebodytext text-center ",
      type: "text",
      width: "",
      isEdit: true,
      nagigate: "detailspage",
    },
    {
      id: 3,
      displayName: "Contact Number",
      mappingName: "contactNumber",
      theadClass: "dynatableheadtext text-center",
      tbodyClass: " dynatablebodytext text-center ",
      type: "text",
      width: "",
      isEdit: true,
      nagigate: "detailspage",
    },
    {
      id: 4,
      displayName: "Representative Name",
      mappingName: "representativeName",
      theadClass: "dynatableheadtext text-center",
      tbodyClass: " dynatablebodytext text-center ",
      type: "text",
      width: "",
      isEdit: true,
      nagigate: "detailspage",
    },
    {
      id: 5,
      displayName: "Status",
      mappingName: "vendorStatus",
      theadClass: "dynatableheadtext text-center",
      tbodyClass: " dynatablebodytext text-center ",
      type: "text",
      width: "",
      isEdit: true,
      nagigate: "detailspage",
    },
  ];

  // Navigate to create form
  const handleNewVendor = () => {
    formik.resetForm();
    formik.setValues({ ...emptyVendor });
    setCurrentView("create");
  };

  // Fetch vendor by ID for view/edit
  const fetchVendorById = async (vId, view) => {
    dispatch(updateLoader(true));
    dispatch(updateSnackbar(initialSnackbar));
    try {
      const res = await getApi(
        `/mgmt/customer/vendor/${vId}?atsAdmin=true`,
        process.env.REACT_APP_USE_MANAGEMENT,
      );
      const jsonData = res.data;
      if (jsonData.code === 200) {
        const vendorArr = jsonData.vendor || jsonData["vendor "];
        if (vendorArr && vendorArr.length > 0) {
          const vendor = vendorArr[0];
          // Map status back to Active/Inactive
          vendor.vendorStatus =
            String(vendor.vendorStatus) === "1" ? "Active" : "Inactive";

          setSelectedVendor(vendor);
          formik.setValues({ ...vendor });
          setCurrentView(view);
          dispatch(updateLoader(false));
        } else {
          dispatch(updateLoader(false));
          dispatch(
            updateSnackbar({
              type: "error",
              message: "Vendor details not found",
            }),
          );
        }
      } else {
        dispatch(updateLoader(false));
        dispatch(
          updateSnackbar({
            type: "error",
            message: jsonData.message || "Failed to fetch vendor details",
          }),
        );
      }
    } catch (error) {
      if (error?.response?.status === 405 || error?.response?.status === 406) {
        return sessionExpired(error?.response?.data?.message);
      }
      dispatch(updateLoader(false));
      dispatch(
        updateSnackbar({
          type: "error",
          message: error?.response?.data?.message || "Network error occurred",
        }),
      );
    }
  };

  // Save new vendor
  const handleSaveNew = () => {
    formik.handleSubmit();
  };

  // Click vendor name → view details
  const handleVendorClick = (vendor) => {
    fetchVendorById(vendor.id, "view");
    setActiveTab("details");
  };

  // Edit button
  const handleEdit = () => {
    setCurrentView("edit");
  };

  // Save edit
  const handleSaveEdit = () => {
    formik.handleSubmit();
  };

  // Cancel → go back
  const handleCancel = () => {
    if (currentView === "create") {
      setCurrentView("list");
      formik.resetForm();
    } else if (currentView === "edit") {
      formik.setValues({ ...selectedVendor });
      setCurrentView("view");
      formik.setTouched({});
    }
  };

  // Back to list from view/edit
  const handleBackToList = () => {
    setSelectedVendor(null);
    setCurrentView("list");
  };

  const onReceiveActionHandler = (type, obj) => {
    if (type === "detailspage") {
      handleVendorClick(obj);
    } else if (type === "edit") {
      fetchVendorById(obj.id, "edit");
      setActiveTab("details");
    } else if (type === "pagination") {
      fetchData(obj);
    } else if (type === "searchResult") {
      setFilterValue(obj.keyword);
      // We need to fetch with the new keyword
      const searchObj = { current: 1 };
      // Since setFilterValue is async, we might need a workaround or just pass the keyword directly
      fetchDataWithKeyword(searchObj, obj.keyword);
    } else if (type === "cancelSearchResult") {
      setFilterValue("");
      let obj1 = { current: 1 };
      fetchDataWithKeyword(obj1, "");
    }
  };

  const fetchDataWithKeyword = async (obj, keyword) => {
    setVendorsList([]);
    dispatch(updateLoader(true));
    try {
      const res = await getApi(
        `/mgmt/customer/vendor?limit=${config.limitData}&offset=${obj.current}&keyword=${keyword}&atsAdmin=true`,
        process.env.REACT_APP_USE_MANAGEMENT,
      );
      const jsonData = res.data;
      if (jsonData.code === 200) {
        const myData = jsonData.vendors;
        if (myData && myData.length > 0) {
          myData.forEach((v) => {
            v.vendorStatus =
              String(v.vendorStatus) === "1" ? "Active" : "Inactive";
          });
        }
        dispatch(updateLoader(false));
        let configure = JSON.parse(JSON.stringify(config));
        configure.totalRecords = jsonData.totalCount;
        configure.currentPage = obj.current;
        setConfig(configure);
        setVendorsList(myData && myData.length > 0 ? myData : "null");
      } else {
        dispatch(updateLoader(false));
        setVendorsList("null");
      }
    } catch (error) {
      if (error?.response?.status === 405 || error?.response?.status === 406) {
        return sessionExpired(error?.response?.data?.message);
      }
      dispatch(updateLoader(false));
      setVendorsList("null");
    }
  };

  // ==================== RENDER FORM FIELDS ====================
  const renderFormFields = (readOnly) => (
    <>
      {!readOnly && <FocusError formik={formik} />}
      <div className={`vendor-form-grid ${readOnly ? "readonly" : ""}`}>
        <TextField
          className="boldlabel"
          label="Vendor Name*"
          name="vendorName"
          value={formik.values.vendorName}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.vendorName && Boolean(formik.errors.vendorName)}
          helperText={formik.touched.vendorName && formik.errors.vendorName}
          variant="outlined"
          InputLabelProps={{ shrink: true }}
          disabled={readOnly}
        />
        <TextField
          className="boldlabel"
          label="Registered Address (HQ)*"
          name="registeredAddressHQ"
          value={formik.values.registeredAddressHQ}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            formik.touched.registeredAddressHQ &&
            Boolean(formik.errors.registeredAddressHQ)
          }
          helperText={
            formik.touched.registeredAddressHQ &&
            formik.errors.registeredAddressHQ
          }
          variant="outlined"
          InputLabelProps={{ shrink: true }}
          disabled={readOnly}
        />
        <TextField
          className="boldlabel"
          label="Website (optional)"
          name="website"
          value={formik.values.website}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.website && Boolean(formik.errors.website)}
          helperText={formik.touched.website && formik.errors.website}
          variant="outlined"
          InputLabelProps={{ shrink: true }}
          disabled={readOnly}
        />
        <TextField
          className="boldlabel"
          label="Contact Number*"
          name="contactNumber"
          value={formik.values.contactNumber}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            formik.touched.contactNumber && Boolean(formik.errors.contactNumber)
          }
          helperText={
            formik.touched.contactNumber && formik.errors.contactNumber
          }
          variant="outlined"
          InputLabelProps={{ shrink: true }}
          disabled={readOnly}
        />
        <TextField
          className="boldlabel"
          label="Representative's Name*"
          name="representativeName"
          value={formik.values.representativeName}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            formik.touched.representativeName &&
            Boolean(formik.errors.representativeName)
          }
          helperText={
            formik.touched.representativeName &&
            formik.errors.representativeName
          }
          variant="outlined"
          InputLabelProps={{ shrink: true }}
          disabled={readOnly}
        />
        <TextField
          className="boldlabel"
          label="Email ID*"
          name="emailId"
          value={formik.values.emailId}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.emailId && Boolean(formik.errors.emailId)}
          helperText={formik.touched.emailId && formik.errors.emailId}
          variant="outlined"
          InputLabelProps={{ shrink: true }}
          disabled={readOnly}
        />
        <TextField
          className="boldlabel"
          label="Country*"
          name="country"
          value={formik.values.country}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.country && Boolean(formik.errors.country)}
          helperText={formik.touched.country && formik.errors.country}
          variant="outlined"
          InputLabelProps={{ shrink: true }}
          disabled={readOnly}
        />
        <FormControl
          variant="outlined"
          className="boldlabel"
          disabled={readOnly}
          error={
            formik.touched.vendorStatus && Boolean(formik.errors.vendorStatus)
          }
        >
          <InputLabel shrink>Vendor Status*</InputLabel>
          <Select
            name="vendorStatus"
            value={formik.values.vendorStatus}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Vendor Status*"
            displayEmpty
          >
            {vendorStatusOptions.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </Select>
          {formik.touched.vendorStatus && formik.errors.vendorStatus && (
            <FormHelperText>{formik.errors.vendorStatus}</FormHelperText>
          )}
        </FormControl>
      </div>

      {/* Vendor Billing Address */}
      <div className="vendor-billing-section">
        <div className="vendor-billing-header">
          <img
            src={
              process.env.PUBLIC_URL + "/assests/img/VendorBillingInfoIcon.svg"
            }
            alt=""
          />
          <span>Vendor Billing Address</span>
        </div>
        <div className={`vendor-form-grid ${readOnly ? "readonly" : ""}`}>
          <TextField
            className="boldlabel"
            label="Billing Address Line 1*"
            name="billingAddressLine1"
            value={formik.values.billingAddressLine1}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.billingAddressLine1 &&
              Boolean(formik.errors.billingAddressLine1)
            }
            helperText={
              formik.touched.billingAddressLine1 &&
              formik.errors.billingAddressLine1
            }
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            disabled={readOnly}
          />
          <TextField
            className="boldlabel"
            label="Billing Address Line 2 (optional)"
            name="billingAddressLine2"
            value={formik.values.billingAddressLine2}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.billingAddressLine2 &&
              Boolean(formik.errors.billingAddressLine2)
            }
            helperText={
              formik.touched.billingAddressLine2 &&
              formik.errors.billingAddressLine2
            }
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            disabled={readOnly}
          />
          <TextField
            className="boldlabel"
            label="City*"
            name="city"
            value={formik.values.city}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.city && Boolean(formik.errors.city)}
            helperText={formik.touched.city && formik.errors.city}
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            disabled={readOnly}
          />
          <TextField
            className="boldlabel"
            label="State*"
            name="state"
            value={formik.values.state}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.state && Boolean(formik.errors.state)}
            helperText={formik.touched.state && formik.errors.state}
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            disabled={readOnly}
          />
          <TextField
            className="boldlabel"
            label="Zipcode*"
            name="zipcode"
            value={formik.values.zipcode}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.zipcode && Boolean(formik.errors.zipcode)}
            helperText={formik.touched.zipcode && formik.errors.zipcode}
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            disabled={readOnly}
          />
        </div>
      </div>
    </>
  );

  // ==================== LIST VIEW ====================
  if (currentView === "list") {
    return (
      <>
        {/* New Vendor button */}
        <div
          className="addNewVendorSection"
          style={{ display: "flex", height: "34px", gap: "10px", top: "190px", alignItems: "center" }}
          onClick={handleNewVendor}
        >
          <DashboardPlusicon width={35} height={36} />
          <h4 className="addnewvendortext">New Vendor</h4>
        </div>

        <div style={{ background: "#fff", padding: "0px 30px 47px" }}>
          {vendorsList.length > 0 && vendorsList !== "null" && (
            <DynamicTable
              config={config}
              data={vendorsList}
              colmns={columnsData}
              actionDropdown={actionsDropdownData}
              actions={actionsList}
              filterValue={filterValue}
              noDataText={"No vendors found"}
              searchKeyWord="Search by Vendor Name, Email ID, Contact Number"
              onReceiveActionProps={(type, obj) =>
                onReceiveActionHandler(type, obj)
              }
            />
          )}
          {(vendorsList.length === 0 || vendorsList === "null") && (
            <DynamicTable
              config={config}
              data={vendorsList}
              colmns={columnsData}
              actionDropdown={actionsDropdownData}
              actions={actionsList}
              filterValue={filterValue}
              noDataText={"No vendors found"}
              searchKeyWord="Search by Vendor Name, Email ID, Contact Number"
              onReceiveActionProps={(type, obj) =>
                onReceiveActionHandler(type, obj)
              }
            />
          )}
        </div>
      </>
    );
  }

  // ==================== CREATE VIEW ====================
  if (currentView === "create") {
    return (
      <div className="vendor-details-container" style={{ paddingTop: "30px" }}>
        {renderFormFields(false)}
        <div
          className="vendor-form-actions"
          style={{ justifyContent: "flex-end", marginTop: "40px" }}
        >
          <Button
            className="buttonHover"
            variant="text"
            style={{
              color: "red",
              marginRight: "25px",
              fontWeight: "bolder",
              textTransform: "capitalize",
              textDecoration: "underline",
            }}
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            className="buttonHover1"
            variant="contained"
            style={{
              backgroundColor: "var(--primary-bg-color)",
              color: "var(--primary-background-text-color)",
              borderRadius: "10px",
              width: "6rem",
              textTransform: "capitalize",
            }}
            onClick={handleSaveNew}
          >
            Save
          </Button>
        </div>
      </div>
    );
  }

  // ==================== VIEW / EDIT MODE ====================
  if (currentView === "view" || currentView === "edit") {
    const isEditing = currentView === "edit";

    return (
      <div className="vendor-details-container" style={{ paddingTop: "20px" }}>
        {/* Top bar: Back + Tabs + Edit/Save */}
        <div className="vendor-detail-topbar">
          <button className="vendor-back-link" onClick={handleBackToList}>
            <ArrowBackIcon /> Back to Vendors
          </button>

          <div className="vendor-toggle-tabs">
            <div
              className={`vendor-toggle-tab ${activeTab === "details" ? "active" : ""}`}
              onClick={() => setActiveTab("details")}
            >
              Vendor Details
            </div>
            <div
              className={`vendor-toggle-tab ${activeTab === "users" ? "active" : ""}`}
              onClick={() => setActiveTab("users")}
            >
              Vendor Users
            </div>
          </div>

          <div
            className="vendor-action-container"
            style={{
              minWidth: "200px",
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
            }}
          >
            {activeTab === "details" &&
              (isEditing ? (
                <div>
                  <Button
                    className="buttonHover"
                    variant="text"
                    style={{
                      color: "red",
                      marginRight: "25px",
                      fontWeight: "bolder",
                      textTransform: "capitalize",
                      textDecoration: "underline",
                    }}
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="buttonHover1"
                    variant="contained"
                    style={{
                      backgroundColor: "#ffd800",
                      color: "black",
                      borderRadius: "10px",
                      width: "6rem",
                      textTransform: "capitalize",
                    }}
                    onClick={handleSaveEdit}
                  >
                    Save
                  </Button>
                </div>
              ) : (
                <div>
                  <Button className="vendor-edit-btn" onClick={handleEdit}>
                    Edit
                  </Button>
                </div>
              ))}
          </div>
        </div>

        {/* Main Content Area */}
        {activeTab === "details" ? (
          renderFormFields(!isEditing)
        ) : (
          // In Vendor Users tab, we render the Venders component
          <div style={{ margin: "-15px -30px" }}>
            <Vendors vendorId={selectedVendor?.id} />
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default VendorPortal;
